import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import socket from "../lib/socket";
import { Headset, PaperPlaneRight, X, ShieldStar } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useNotificationSound } from "../lib/useNotificationSound";
import { BRAND } from "../lib/brand";

function formatTime(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es }); }
  catch { return ""; }
}

export default function SupportChatWidget() {
  const { user } = useAuth();
  const [open, setOpen]             = useState(false);
  const [messages, setMessages]     = useState([]);
  const [text, setText]             = useState("");
  const [unread, setUnread]         = useState(0);
  const [connected, setConnected]   = useState(false);
  const playSound = useNotificationSound();

  // Use refs so callbacks always see the latest values without re-registering
  const openRef         = useRef(open);
  const historyLoadedRef= useRef(false);
  const bottomRef       = useRef(null);
  const inputRef        = useRef(null);

  useEffect(() => { openRef.current = open; }, [open]);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // ── Tab title with unread count ────────────────────────────────────────────
  useEffect(() => {
    const base = BRAND.name;
    if (unread > 0) {
      document.title = `(${unread}) ${base}`;
    } else {
      document.title = base;
    }
    return () => { document.title = base; };
  }, [unread]);

  // ── Register socket listeners ONCE per user session ──────────────────────
  useEffect(() => {
    if (!user || user.role === "admin") return;

    const onConnect = () => {
      setConnected(true);
      if (openRef.current) socket.emit("support:open");
    };
    const onDisconnect = () => setConnected(false);

    const onHistory = ({ messages: msgs }) => {
      setMessages(msgs || []);
      historyLoadedRef.current = true;
      setTimeout(scrollToBottom, 60);
    };

    const onMessage = (msg) => {
      setMessages((prev) => {
        if (prev.find((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      // Sound + unread badge when message is from admin and widget is closed
      if (msg.sender_role === "admin") {
        if (!openRef.current) {
          setUnread((n) => n + 1);
          playSound();
        } else {
          // Widget is open — still play a subtle sound
          playSound();
        }
      }
      setTimeout(scrollToBottom, 60);
    };

    const onUnread = ({ count }) => setUnread(count);

    socket.on("connect",          onConnect);
    socket.on("disconnect",       onDisconnect);
    socket.on("support:history",  onHistory);
    socket.on("support:message",  onMessage);
    socket.on("support:unread",   onUnread);

    if (socket.connected) {
      setConnected(true);
    } else {
      socket.connect();
    }

    return () => {
      socket.off("connect",         onConnect);
      socket.off("disconnect",      onDisconnect);
      socket.off("support:history", onHistory);
      socket.off("support:message", onMessage);
      socket.off("support:unread",  onUnread);
    };
  }, [user, scrollToBottom, playSound]); // ← NO "open" here — avoids listener teardown on toggle

  const handleOpen = () => {
    setOpen(true);
    openRef.current = true;
    setUnread(0);
    if (!socket.connected) socket.connect();
    socket.emit("support:open");
    setTimeout(scrollToBottom, 120);
  };

  const handleClose = () => {
    setOpen(false);
    openRef.current = false;
  };

  const sendMessage = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !connected) return;
    socket.emit("support:send", { text: trimmed });
    setText("");
    inputRef.current?.focus();
  };

  if (!user || user.role === "admin") return null;

  return (
    <>
      {/* Chat Panel */}
      {open && (
        <div
          data-testid="support-chat-panel"
          className="fixed z-50 flex flex-col support-panel-pos"
          style={{
            left: "1rem",
            width: "320px",
            height: "420px",
            background: "#111113",
            border: "1px solid rgba(212,175,55,0.2)",
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.05)",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              background: "rgba(212,175,55,0.05)",
              borderRadius: "16px 16px 0 0",
            }}
          >
            <div className="flex items-center gap-2.5">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.3)" }}
              >
                <Headset className="w-4 h-4" style={{ color: "#D4AF37" }} weight="fill" />
              </div>
              <div>
                <div className="text-sm font-bold text-white leading-tight">Soporte</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: connected ? "#22C55E" : "#71717A" }} />
                  <span className="text-[9px] uppercase tracking-widest" style={{ color: connected ? "#22C55E" : "#52525B" }}>
                    {connected ? "En línea" : "Conectando"}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              data-testid="support-close-btn"
              className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-white/10"
              style={{ color: "#71717A" }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3" style={{ minHeight: 0 }}>
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full gap-2 opacity-50 text-center px-4">
                <Headset className="w-8 h-8" style={{ color: "#D4AF37" }} />
                <p className="text-xs text-zinc-500">
                  Escríbenos con cualquier duda. Nuestro equipo te responderá pronto.
                </p>
              </div>
            )}
            {messages.map((msg) => {
              const isAdmin = msg.sender_role === "admin";
              const isOwn   = !isAdmin;
              return (
                <div key={msg.id} className={`flex gap-2 mb-2.5 ${isOwn ? "flex-row-reverse" : ""}`}>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
                    style={{
                      background: isAdmin ? "linear-gradient(135deg,#D4AF37,#A07C20)" : "rgba(255,255,255,0.1)",
                      color: isAdmin ? "#000" : "#fff",
                    }}
                  >
                    {isAdmin ? <ShieldStar className="w-3.5 h-3.5" weight="fill" /> : (user?.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className={`flex flex-col max-w-[75%] ${isOwn ? "items-end" : "items-start"}`}>
                    <div
                      className="px-3 py-2 rounded-xl text-xs leading-relaxed"
                      style={isAdmin ? {
                        background: "rgba(212,175,55,0.08)",
                        border: "1px solid rgba(212,175,55,0.2)",
                        borderLeft: "3px solid #D4AF37",
                        color: "#fff",
                        borderRadius: "4px 12px 12px 12px",
                      } : {
                        background: "rgba(212,175,55,0.18)",
                        color: "#fff",
                        borderRadius: "12px 4px 12px 12px",
                      }}
                    >
                      {msg.text}
                    </div>
                    <span className="text-[9px] mt-1 px-1" style={{ color: "rgba(255,255,255,0.3)" }}>
                      {formatTime(msg.created_at)}
                    </span>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div
            className="flex-shrink-0 px-3 pb-3"
            style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "10px" }}
          >
            <form onSubmit={sendMessage} className="flex gap-2">
              <input
                ref={inputRef}
                data-testid="support-input"
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={2000}
                placeholder={connected ? "Escribe tu consulta..." : "Conectando..."}
                disabled={!connected}
                className="flex-1 px-3 py-2 rounded-lg text-xs outline-none"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                }}
                onFocus={(e) => (e.target.style.borderColor = "rgba(212,175,55,0.4)")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
              />
              <button
                type="submit"
                data-testid="support-send-btn"
                disabled={!text.trim() || !connected}
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
                style={{
                  background: text.trim() && connected ? "#D4AF37" : "rgba(255,255,255,0.06)",
                  color: text.trim() && connected ? "#000" : "#52525B",
                }}
              >
                <PaperPlaneRight className="w-3.5 h-3.5" weight="fill" />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Styles: FAB pulse + positioning */}
      <style>{`
        .support-fab-pos   { bottom: 52px; }
        .support-panel-pos { bottom: 100px; }
        @media (min-width: 1024px) {
          .support-fab-pos   { bottom: 20px; }
          .support-panel-pos { bottom: 80px; }
        }
        @keyframes support-pulse {
          0%   { box-shadow: 0 4px 20px rgba(212,175,55,0.35), 0 0 0 0 rgba(239,68,68,0.5); }
          60%  { box-shadow: 0 4px 20px rgba(212,175,55,0.35), 0 0 0 10px rgba(239,68,68,0); }
          100% { box-shadow: 0 4px 20px rgba(212,175,55,0.35), 0 0 0 0 rgba(239,68,68,0); }
        }
        .support-fab-pulse {
          animation: support-pulse 1.8s ease-out infinite !important;
        }
      `}</style>

      {/* Floating Button */}
      <button
        data-testid="support-fab"
        onClick={open ? handleClose : handleOpen}
        className={`fixed z-50 flex items-center gap-2 px-3.5 py-2.5 rounded-full font-bold text-xs transition-all support-fab-pos ${!open && unread > 0 ? "support-fab-pulse" : ""}`}
        style={{
          left: "1rem",
          background: open ? "rgba(212,175,55,0.15)" : "#D4AF37",
          color: open ? "#D4AF37" : "#000",
          border: open ? "1px solid rgba(212,175,55,0.4)" : "none",
          boxShadow: open ? "none" : "0 4px 20px rgba(212,175,55,0.35)",
        }}
      >
        <Headset className="w-4 h-4 flex-shrink-0" weight="fill" />
        <span>Soporte</span>
        {unread > 0 && (
          <span
            data-testid="support-unread-badge"
            className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black"
            style={{ background: "#EF4444", color: "#fff" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
    </>
  );
}
